/* eslint-disable consistent-return */
import { useCallback } from 'react';
import type { IQuoteResponse } from '@/common/interfaces/quoter.interface';
import {
  getDesignGroupCartPayloadWithPrices,
  mapDesignItemToCartPayload,
} from '@/utilities/CartUtils';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  cartQuery,
  useAddCartItemMutation,
  useRemoveCartItemMutation,
  useUpdateCartItemMutation,
  useGetCartItemsQuery,
  useRemoveStoreItemMutation,
  useUpdateCartItemsMutation,
} from '@/api/CartQuery';
import type {
  ICartCountResponse,
  ICartItemPayload,
  ICartResponse,
} from '@/common/interfaces/cart.interface';
import {
  disableAddCartSuccessMessage,
  enableAddCartSuccessMessage,
  setCartCountObject,
  updateCartCountObject,
} from '@/store/reducers/cart';
import {
  disableAddStoreSuccessMessage,
  enableAddStoreSuccessMessage,
  resetStoreCartState,
  setStoreCountObject,
  setStoreItems,
  updateStoreCountObject,
} from '@/store/reducers/storeCart';
import {
  filterRemovedStoreCartItems,
  matchesStoreCartItemId,
  resolveStoreCartApiItemIds,
  resolveStoreCartItemIdsFromState,
} from '@/modules/Cart/StoreCart/helpers/storeCartRemoval.utils';
import type { RootState } from '@/store';
import { useLazyGetDesignItemQuery } from '@/api/DesignQuery';
import { showToastrMessage } from '@/utilities/Toastr.Utils';
import { getVersionNumber } from '@/utilities/DesignUtils.misc';
import { captureAddedToStoreOrCart } from '@/mixpanel/MixpanelEvents';
import {
  getDesignDetailPricingReqPayload,
  fetchDesignGroupQuotes,
  getDesignGroupUnitPricesFromQuotes,
  resolveQuotedCartUnitPrice,
} from '@/utilities/PricingUtils';
import { getExpeditedDeliveryDateStrFromAdditionalShipping } from '@/utilities/ShippingUtils';
import { useGetQuoteMutation } from '@/api/QuoterQuery';
import { clPushAddedToCart, clPushAddedToStore } from '@/CustomerLabs/ClEvents';
import { buildClProductProperties } from '@/CustomerLabs/ClUtils';
import { useBuildCommonDataLayer } from '@/CustomerLabs/useBuildCommonDataLayer';
import { CL_EVENT_NAMES } from '@/constants/CustomerLabs';
import { markDesignForDefaultSizePrefill } from '@/utilities/cartSizeQuantityStorage';

export interface ICartInfoUpdatePayload {
  quantity?: number;
  price?: number;
  apparelSizes?: string;
  /** Qty per size (e.g. { S: 8, M: 4 }); backend derives quantity from its sum. */
  sizeQuantities?: Record<string, number>;
  notes?: string;
  is_selected?: 0 | 1;
}

export type AddToCartOutcome = 'added' | 'already_exists' | 'failed';

interface ICartItemsUpdatePayload {
  ship_type: string;
  price?: number;
}

const getCartItemProductProperties = (items: ICartItemPayload[]) =>
  buildClProductProperties(
    items.map((item) => ({
      name: item.apparelName || item.apparelBrand,
      styleCode: item.apparelStyleCode,
      brandName: item.apparelBrand,
      colorName: item.apparelColor,
      price: item.price,
      quantity: item.quantity,
    }))
  );

/**
 * Hook to perform action related to cart or store
 * Also responsible for updating the cart in redux store
 */
export const useCartActions = () => {
  const getCommonDataLayer = useBuildCommonDataLayer();
  const [addCartItem] = useAddCartItemMutation();
  const [removeCartItem] = useRemoveCartItemMutation();
  const [removeStoreItem] = useRemoveStoreItemMutation();
  const [updateCartItem] = useUpdateCartItemMutation();
  const [updateCartItems] = useUpdateCartItemsMutation();
  const [fetchDesignItem] = useLazyGetDesignItemQuery();
  const { refetch: refetchCartCount } = useGetCartItemsQuery({
    countAndTotalOnly: true,
  });
  const { refetch: refetchStoreCount } = useGetCartItemsQuery({
    countAndTotalOnly: true,
    isStore: true,
  });
  const { refetch: refetchCartItems } = useGetCartItemsQuery({
    countAndTotalOnly: false,
  });
  const { refetch: refetchStoreCartItems } = useGetCartItemsQuery({
    countAndTotalOnly: false,
    isStore: true,
  });
  const dispatch = useAppDispatch();
  const { cartItems, cartCountObject } = useAppSelector((state) => state.cart);
  const { user } = useAppSelector((state) => state.user);
  const { storeItems, storeCountObject } = useAppSelector((state) => state.storeCart);
  const [getQuote] = useGetQuoteMutation();

  const timer = 1500;

  /**
   * Function to set timer for add cart success message
   */
  const setTimedAddCartSuccessMessage = useCallback(() => {
    dispatch(enableAddCartSuccessMessage());

    setTimeout(() => {
      dispatch(disableAddCartSuccessMessage());
    }, timer);
  }, [dispatch]);

  /**
   * Function to set timer for add store success message
   */
  const setTimeAddStoreSuccessMessage = useCallback(() => {
    dispatch(enableAddStoreSuccessMessage());

    setTimeout(() => {
      dispatch(disableAddStoreSuccessMessage());
    }, timer);
  }, [dispatch]);

  /**
   * Function to refetch cart count object
   */
  const refetchCartCountObject = useCallback(async () => {
    try {
      const [cartCountResponse, storeCountResponse] = await Promise.all([
        refetchCartCount(),
        refetchStoreCount(),
      ]);

      if ('data' in cartCountResponse && cartCountResponse.data) {
        dispatch(setCartCountObject(cartCountResponse.data));
      }

      if ('data' in storeCountResponse && storeCountResponse.data) {
        dispatch(setStoreCountObject(storeCountResponse.data));
      }
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, refetchCartCount, refetchStoreCount]);

  /**
   * Function to get all cart items information
   * @returns Promise with cart items data or null if error
   */
  const getCartItems = useCallback(async () => {
    try {
      const response = await refetchCartItems();

      if ('data' in response) {
        return response.data?.cartItems;
      }

      return null;
    } catch (error) {
      console.error('Error fetching cart items:', error);
      return null;
    }
  }, [refetchCartItems]);

  /**
   * Refetch cart and store cart items after closing the store cart overlay so
   * minimums and other persisted changes are reflected across the app.
   */
  const refreshCartDataOnStoreCartClose = useCallback(async () => {
    try {
      dispatch(cartQuery.util.invalidateTags(['Cart', 'CartCount']));

      const [cartItemsResponse, storeCartItemsResponse] = await Promise.all([
        refetchCartItems(),
        refetchStoreCartItems(),
      ]);

      if ('data' in storeCartItemsResponse && storeCartItemsResponse.data?.cartItems) {
        dispatch(setStoreItems(storeCartItemsResponse.data.cartItems));
      }

      await refetchCartCountObject();

      if ('data' in cartItemsResponse) {
        return cartItemsResponse.data?.cartItems ?? null;
      }

      return null;
    } catch (error) {
      console.error('Error refreshing cart data on store cart close:', error);
      return null;
    }
  }, [dispatch, refetchCartItems, refetchStoreCartItems, refetchCartCountObject]);

  /**
   * Function to add item to cart or store in DB.
   *
   * Also updates the cart count object accordingly
   * @param data - cart item payload data
   */
  const addToCart = useCallback(
    async (data: ICartItemPayload) => {
      try {
        const cartPayload: ICartItemPayload = data.isStore ? { ...data, quantity: 0 } : data;
        const response = await addCartItem({ data: cartPayload });

        if ('error' in response) {
          await refetchCartCountObject();
          const errorData = response.error as { data: { message?: string } };

          if (errorData.data.message) {
            showToastrMessage(errorData.data.message, 'failure');
          } else {
            showToastrMessage('Something went wrong!', 'failure');
          }

          return await Promise.reject(new Error(JSON.stringify(response)));
        }

        if ('data' in response) {
          captureAddedToStoreOrCart(
            cartPayload.isStore ? 'Store' : 'Cart',
            cartPayload.alphaNumericProofItemId,
            cartPayload.isStore
              ? undefined
              : (getVersionNumber(cartPayload.proofItemVersionName) as string)
          );

          const estOrderValue = cartPayload.quantity * cartPayload.price;
          const eventName = cartPayload.isStore
            ? CL_EVENT_NAMES.ADDED_TO_STORE
            : CL_EVENT_NAMES.ADDED_TO_CART;
          const common = getCommonDataLayer(eventName);
          const productProperties = getCartItemProductProperties([cartPayload]);

          if (cartPayload.isStore) {
            clPushAddedToStore(common, estOrderValue, productProperties);
          } else {
            clPushAddedToCart(common, estOrderValue, productProperties);
          }

          dispatch(setCartCountObject(response.data));
          if (cartPayload.isStore) {
            dispatch(setStoreCountObject(response.data));
          } else {
            markDesignForDefaultSizePrefill(user?.id, data.designId);
          }
        }

        if (cartPayload.isStore) {
          setTimeAddStoreSuccessMessage();
        } else {
          setTimedAddCartSuccessMessage();
        }
      } catch (error) {
        if (error instanceof Error) {
          return Promise.reject(new Error(error.message));
        }

        return Promise.reject(new Error('Something went wrong!'));
      }
    },
    [
      addCartItem,
      refetchCartCountObject,
      dispatch,
      setTimeAddStoreSuccessMessage,
      setTimedAddCartSuccessMessage,
      getCommonDataLayer,
      user?.id,
    ]
  );

  const addMultipleToCart = useCallback(
    async (
      data: ICartItemPayload[],
      latestCartCountObject?: ICartCountResponse,
      latestCartItems?: ICartResponse,
      totalEstOrderValue?: number
    ): Promise<AddToCartOutcome> => {
      try {
        /** Separate items into new items and existing items that need quantity updates */
        const newItems: ICartItemPayload[] = [];
        const itemsToUpdate: { cartItemId: number; newQuantity: number; item: ICartItemPayload }[] =
          [];
        const isStoreFlow = data.length > 0 && data[0].isStore;
        const cartDesignIds = isStoreFlow
          ? latestCartCountObject?.storeDesignIds ||
            storeCountObject?.storeDesignIds ||
            cartCountObject?.storeDesignIds ||
            []
          : latestCartCountObject?.cartDesignIds || cartCountObject?.cartDesignIds || [];
        const cartItemsToProcess = isStoreFlow
          ? latestCartItems || storeItems
          : latestCartItems || cartItems;

        data.forEach((item) => {
          const existingCartItem = cartItemsToProcess?.find(
            (cartItem) =>
              cartItem.designId === item.designId && cartDesignIds.includes(cartItem.designId)
          );

          if (existingCartItem) {
            // Store cart creates always send quantity 0; never overwrite an existing line on re-add.
            if (isStoreFlow) {
              return;
            }

            // Item exists in cart, check if quantity is different
            if (existingCartItem.quantity !== item.quantity) {
              itemsToUpdate.push({
                cartItemId: existingCartItem.id,
                newQuantity: item.quantity,
                item,
              });
            }
          } else {
            // Item doesn't exist in cart, add to new items
            newItems.push(isStoreFlow ? { ...item, quantity: 0 } : item);
          }
        });

        let hasSuccessfulOperations = false;
        let successfulUpdatedItems: ICartItemPayload[] = [];
        let successfulNewItems: ICartItemPayload[] = [];

        // Update existing items with new quantities
        if (itemsToUpdate.length > 0) {
          const updateResults = await Promise.allSettled(
            itemsToUpdate.map(({ cartItemId, newQuantity }) =>
              updateCartItem({ cartItemId, data: { quantity: newQuantity } })
            )
          );

          const anyUpdateSuccessful = updateResults.some(
            (result) =>
              result.status === 'fulfilled' &&
              result.value &&
              typeof result.value === 'object' &&
              'data' in result.value
          );
          if (anyUpdateSuccessful) {
            hasSuccessfulOperations = true;
          }

          successfulUpdatedItems = itemsToUpdate
            .filter((_, index) => {
              const result = updateResults?.[index];
              return (
                result.status === 'fulfilled' &&
                result.value &&
                typeof result.value === 'object' &&
                'data' in result.value
              );
            })
            .map(({ item }) => item);
        }

        // Add new items
        if (newItems.length > 0) {
          const addResults = await Promise.allSettled(
            newItems.map((item) => addCartItem({ data: item }))
          );

          const anyAddSuccessful = addResults.some(
            (result) =>
              result.status === 'fulfilled' &&
              result.value &&
              typeof result.value === 'object' &&
              'data' in result.value
          );
          if (anyAddSuccessful) {
            hasSuccessfulOperations = true;
          }

          successfulNewItems = newItems.filter((_, index) => {
            const result = addResults?.[index];
            return (
              result.status === 'fulfilled' &&
              result.value &&
              typeof result.value === 'object' &&
              'data' in result.value
            );
          });

          if (!data[0]?.isStore) {
            successfulNewItems.forEach((item) => {
              markDesignForDefaultSizePrefill(user?.id, item.designId);
            });
          }
        }

        // Show appropriate success/error messages
        if (hasSuccessfulOperations) {
          if (data.length > 0) {
            captureAddedToStoreOrCart(
              data[0].isStore ? 'Store' : 'Cart',
              data[0].alphaNumericProofItemId,
              data[0].isStore
                ? undefined
                : (getVersionNumber(data[0].proofItemVersionName) as string)
            );

            const changedItems = successfulNewItems.concat(successfulUpdatedItems);

            const estOrderValue =
              totalEstOrderValue ??
              changedItems.reduce((sum, item) => sum + item.quantity * item.price, 0);

            const eventName = data[0].isStore
              ? CL_EVENT_NAMES.ADDED_TO_STORE
              : CL_EVENT_NAMES.ADDED_TO_CART;
            const common = getCommonDataLayer(eventName);
            const productProperties = getCartItemProductProperties(changedItems);

            if (data[0].isStore) {
              clPushAddedToStore(common, estOrderValue, productProperties);
            } else {
              clPushAddedToCart(common, estOrderValue, productProperties);
            }

            if (data[0].isStore) {
              setTimeAddStoreSuccessMessage();
            } else {
              setTimedAddCartSuccessMessage();
            }
          }

          await refetchCartCountObject();
          return 'added';
        }

        if (newItems.length === 0 && itemsToUpdate.length === 0) {
          showToastrMessage(
            isStoreFlow
              ? 'Design already added to your store'
              : 'Design already added to your cart',
            'failure'
          );
          await refetchCartCountObject();
          return 'already_exists';
        }

        showToastrMessage('Something went wrong!', 'failure');
        await refetchCartCountObject();
        return 'failed';
      } catch (error) {
        console.error('Error in addMultipleToCart:', error);
        showToastrMessage('Something went wrong!', 'failure');
        return Promise.reject(new Error('Something went wrong!'));
      }
    },
    [
      addCartItem,
      updateCartItem,
      cartItems,
      cartCountObject,
      storeItems,
      storeCountObject,
      refetchCartCountObject,
      setTimeAddStoreSuccessMessage,
      setTimedAddCartSuccessMessage,
      getCommonDataLayer,
      user?.id,
    ]
  );

  /**
   * Function to add multiple items to store in DB.
   */
  const addMultipleToStore = useCallback(
    async (data: ICartItemPayload[], totalEstOrderValue?: number): Promise<AddToCartOutcome> => {
      const storePayloads = data.map((item) => ({ ...item, isStore: true, quantity: 0 }));

      return addMultipleToCart(storePayloads, storeCountObject, storeItems, totalEstOrderValue);
    },
    [addMultipleToCart, storeCountObject, storeItems]
  );

  /**
   * Function to add item to cart or store in DB using proofId and proofItemId
   * @param proofId - proof request id
   * @param proofItemId - proof item id
   * @param isStore - is store item
   */
  const addToCartUsingId = useCallback(
    async (
      proofId: number,
      proofItemId: number,
      alphaNumericProofItemId: string,
      proofItemVersionName: string,
      quantity = 0,
      isStore = false,
      isDesignGroupFlowEnabled = false,
      latestCartCountObject?: ICartCountResponse,
      latestCartItems?: ICartResponse
    ): Promise<AddToCartOutcome | undefined> => {
      try {
        const response = await fetchDesignItem({
          proofId,
          proofItemId,
        });

        if ('data' in response) {
          const timelineChangesEnabled = !!user?.switches?.TIMELINE_GUARANTEE_CHANGES;

          if (isDesignGroupFlowEnabled) {
            const designItem = response.data.proofItem[0];
            const quotes = await fetchDesignGroupQuotes(designItem);
            const unitPrices = getDesignGroupUnitPricesFromQuotes(designItem, quotes);
            const designGroupData = getDesignGroupCartPayloadWithPrices(
              designItem,
              isStore,
              unitPrices,
              undefined
            );
            const shippingRows = Object.values(quotes).flatMap(
              (quote) => quote.additionalShippingPriceObject ?? []
            );
            const expeditedDeliveryDate = getExpeditedDeliveryDateStrFromAdditionalShipping(
              shippingRows,
              timelineChangesEnabled
            );

            designGroupData.forEach((item) => {
              item.expeditedDeliveryDate = expeditedDeliveryDate;
            });

            return await addMultipleToCart(designGroupData, latestCartCountObject, latestCartItems);
          }
          const designData = mapDesignItemToCartPayload(response.data.proofItem[0], isStore);
          if (quantity < 1) {
            const includesMto = (response.data.proofItem[0].apparel?.color || '')
              .toLowerCase()
              .includes('mto');
            quantity = includesMto ? 50 : 12;
          }
          const payload = getDesignDetailPricingReqPayload(response.data.proofItem[0], quantity);
          const isNonZeroQuantity = payload.apparels.find((a) => a.quantity.some((q) => !q));
          if (isNonZeroQuantity) return;

          const quoterResponse = await getQuote({ data: payload });

          if (response && 'data' in quoterResponse) {
            const quoteBreakdown: IQuoteResponse = quoterResponse.data[0];
            const discount = Number(
              (response.data.proofItem[0].design?.designPricing as { discount?: number })
                ?.discount ?? 0
            );
            designData.price =
              resolveQuotedCartUnitPrice({ discount }, quoteBreakdown) ?? designData.price;
            designData.expeditedDeliveryDate = getExpeditedDeliveryDateStrFromAdditionalShipping(
              quoteBreakdown.additionalShippingPriceObject,
              timelineChangesEnabled
            );
          }
          /**
           * This information is required to add the item to the cart and to track the mixpanel event data.
           * Store cart creates send quantity as 0; minimum is chosen later in the store cart.
           */
          designData.quantity = isStore ? 0 : quantity;
          designData.alphaNumericProofItemId = alphaNumericProofItemId;
          designData.proofItemVersionName = proofItemVersionName;

          await addToCart(designData);
          return 'added';
        }

        return undefined;
      } catch (error) {
        if (error instanceof Error) {
          return Promise.reject(new Error(error.message));
        }

        return Promise.reject(new Error('Something went wrong!'));
      }
    },
    [
      addToCart,
      fetchDesignItem,
      getQuote,
      addMultipleToCart,
      user?.switches?.TIMELINE_GUARANTEE_CHANGES,
    ]
  );

  /**
   * Resets cart & cart count object
   */
  const resetCartItems = useCallback(
    async (cartIds = []) => {
      let cartIdsToDelete;

      if (cartIds.length === 0) {
        cartIdsToDelete = cartItems?.map((item) => item.id).join(',') || '';
      } else {
        cartIdsToDelete = cartIds.map((id) => id).join(',');
      }

      try {
        const response = await removeCartItem({ cartItemId: cartIdsToDelete });

        if ('data' in response) {
          dispatch(setCartCountObject(response.data));
        }
      } catch (error) {
        console.error(error);
      }
    },
    [cartItems, dispatch, removeCartItem]
  );

  const clearLocalStoreCartState = useCallback(() => {
    dispatch(resetStoreCartState());
    try {
      dispatch(cartQuery.util.invalidateTags(['Cart', 'CartCount']));
      dispatch(
        cartQuery.util.updateQueryData('getStoreItems', { isStore: true }, (draft) => {
          if (!draft?.cartItems?.length) {
            return;
          }

          draft.cartItems = [];
        })
      );
    } catch (error) {
      console.error(error);
    }
  }, [dispatch]);

  /**
   * Removes all store cart items from the API and clears local store cart state.
   */
  const resetStoreCartItems = useCallback(
    async (explicitCartItemIds: number[] = []) => {
      let storeCartItemIds = explicitCartItemIds.filter((id) => Number.isFinite(id) && id > 0);

      if (!storeCartItemIds.length) {
        try {
          const storeCartItemsResponse = await refetchStoreCartItems();

          if ('data' in storeCartItemsResponse && storeCartItemsResponse.data?.cartItems?.length) {
            storeCartItemIds = resolveStoreCartApiItemIds(storeCartItemsResponse.data.cartItems);
          }
        } catch (error) {
          console.error(error);
        }
      }

      if (!storeCartItemIds.length) {
        storeCartItemIds = resolveStoreCartItemIdsFromState(storeItems ?? []);
      }

      if (!storeCartItemIds.length) {
        clearLocalStoreCartState();
        await refetchCartCountObject();
        return;
      }

      try {
        const response = await removeStoreItem({
          cartItemId: storeCartItemIds.join(','),
        });

        if ('data' in response && response.data) {
          dispatch(setCartCountObject(response.data));
          dispatch(setStoreCountObject(response.data));
        } else {
          await refetchCartCountObject();
        }

        clearLocalStoreCartState();
      } catch (error) {
        console.error(error);
      }
    },
    [
      clearLocalStoreCartState,
      dispatch,
      refetchCartCountObject,
      refetchStoreCartItems,
      removeStoreItem,
      storeItems,
    ]
  );

  /**
   * Return the cart item from the database.
   */
  const removeStoreItemFromStoreItemsCache = useCallback(
    (cartItemId: string) => {
      dispatch(
        cartQuery.util.updateQueryData('getStoreItems', { isStore: true }, (draft) => {
          if (!draft?.cartItems?.length) {
            return;
          }

          draft.cartItems = filterRemovedStoreCartItems(draft.cartItems, cartItemId);
        })
      );
    },
    [dispatch]
  );

  const syncStoreStateAfterItemRemoval = useCallback(
    (cartItemId: string) => {
      removeStoreItemFromStoreItemsCache(cartItemId);

      dispatch((_, getState) => {
        const state = getState() as RootState;
        const currentStoreItems = state.storeCart.storeItems;
        const removedItem = currentStoreItems.find((item) =>
          matchesStoreCartItemId(item, cartItemId)
        );
        const updatedStoreItems = filterRemovedStoreCartItems(currentStoreItems, cartItemId);

        if (updatedStoreItems.length !== currentStoreItems.length) {
          dispatch(setStoreItems(updatedStoreItems));
        }

        const removedDesignId = removedItem?.designId;
        if (!removedDesignId) {
          return;
        }

        const designStillInStore = updatedStoreItems.some(
          (item) => item.designId === removedDesignId
        );
        const nextStoreCount = Math.max(0, (state.storeCart.storeCountObject?.storeCount ?? 0) - 1);
        const pruneStoreDesignIds = (storeDesignIds: number[] = []) =>
          designStillInStore
            ? storeDesignIds
            : storeDesignIds.filter((designId) => designId !== removedDesignId);

        dispatch(
          updateStoreCountObject({
            storeCount: nextStoreCount,
            storeDesignIds: pruneStoreDesignIds(state.storeCart.storeCountObject?.storeDesignIds),
          })
        );
        dispatch(
          updateCartCountObject({
            storeCount: nextStoreCount,
            storeDesignIds: pruneStoreDesignIds(state.cart.cartCountObject?.storeDesignIds),
          })
        );
      });
    },
    [dispatch, removeStoreItemFromStoreItemsCache]
  );

  const removeStoreItemInfo = useCallback(
    async (id: string) => {
      try {
        const response = await removeStoreItem({ cartItemId: id });

        if ('data' in response) {
          syncStoreStateAfterItemRemoval(id);

          if (response.data) {
            dispatch(setCartCountObject(response.data));
            dispatch(setStoreCountObject(response.data));
          } else {
            await refetchCartCountObject();
          }

          return 'success';
        }

        if ('error' in response) {
          showToastrMessage('Failed to remove item from store', 'failure');

          return 'error';
        }
      } catch (error) {
        showToastrMessage('Failed to remove item from store', 'failure');
        return 'error';
      }
    },
    [dispatch, removeStoreItem, syncStoreStateAfterItemRemoval, refetchCartCountObject]
  );

  /**
   * Function to update cart Item info
   * @param cartItemId - cart item id
   * @param data - update payload
   */
  const updateCartInfo = async (cartItemId: number, data: ICartInfoUpdatePayload) => {
    await updateCartItem({ cartItemId, data }).unwrap();
  };

  /**
   * Function to update cart Item info
   * @param cartItemId - cart item id
   * @param note - update notes
   */
  const updateCartNote = async (cartItemId: number, note: string) => {
    try {
      await updateCartItem({ cartItemId, data: { notes: note } });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Function to update all cart Items info based on client id
   * @param clientId - client id
   * @param data - data to update
   */
  const updateCartItemsInfo = async (clientId: number, data: Partial<ICartItemsUpdatePayload>) => {
    try {
      await updateCartItems({ clientId, data });
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Function to remove cart item
   * @param cartItemId - cart item id
   */
  const removeCartInfo = async (cartItemId: number) => {
    try {
      const response = await removeCartItem({ cartItemId });

      if ('data' in response) {
        dispatch(setCartCountObject(response.data));

        return { status: 'success', isCartEmpty: !response.data.cartCount };
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  /**
   * Function to add item to store in DB.
   * Similar to addToCart but specifically for store items
   * @param data - cart item payload data with isStore: true
   */
  const addToStore = useCallback(
    async (data: ICartItemPayload) => {
      // Ensure isStore is true
      const storePayload = { ...data, isStore: true };

      try {
        const response = await addCartItem({ data: storePayload });

        if ('error' in response) {
          await refetchCartCountObject();
          const errorData = response.error as { data: { message?: string } };

          if (errorData.data.message) {
            showToastrMessage(errorData.data.message, 'failure');
          } else {
            showToastrMessage('Something went wrong!', 'failure');
          }

          return await Promise.reject(new Error(JSON.stringify(response)));
        }

        if ('data' in response) {
          captureAddedToStoreOrCart(
            'Store',
            data.alphaNumericProofItemId,
            getVersionNumber(data.proofItemVersionName) as string
          );

          dispatch(setCartCountObject(response.data));
        }

        dispatch(enableAddStoreSuccessMessage());
        setTimeout(() => {
          dispatch(disableAddStoreSuccessMessage());
        }, timer);
      } catch (error) {
        if (error instanceof Error) {
          return Promise.reject(new Error(error.message));
        }

        return Promise.reject(new Error('Something went wrong!'));
      }
    },
    [addCartItem, refetchCartCountObject, dispatch]
  );

  /**
   * Function to refetch store count object
   */
  const refetchStoreCountObject = useCallback(async () => {
    try {
      const response = await refetchCartCount();

      if ('data' in response) {
        dispatch(setCartCountObject(response.data));
      }
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, refetchCartCount]);

  return {
    resetCartItems,
    resetStoreCartItems,
    addToCart,
    addToStore,
    addToCartUsingId,
    updateCartInfo,
    updateCartItemsInfo,
    updateCartNote,
    removeCartInfo,
    removeStoreItemInfo,
    refetchCartCountObject,
    refetchStoreCountObject,
    addMultipleToCart,
    addMultipleToStore,
    getCartItems,
    refreshCartDataOnStoreCartClose,
  };
};
